// src/components/AiTrialPeriod/AiTrialPeriod.jsx
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  doc,
  getDoc,
  setDoc,
  serverTimestamp,
  Timestamp,
  onSnapshot
} from 'firebase/firestore';
import { db } from '../../firebase/firebaseConfig';
import { useAuth } from '../../firebase/AuthContext';
import AiOverLay from '../../pages/UserTools/AI_UserTools/AiOverLay';
import './AiTrialPeriod.css';

const SKIP_KEY = 'phn:skipTrialRedirect';
const TRIAL_ACTIVE_SS = 'phn:trialActiveSession';
const REDIRECT_PATH = '/home';
const MAX_TIMEOUT_MS = 2147483647;

const USERS_COLLECTION = 'users';
const ACCOUNT_DELETED_DOC_ID = 'accountDeleted';

/**
 * Normalize an email for consistent matching.
 */
function normalizeEmail(email) {
  return (email || '').trim().toLowerCase();
}

/**
 * Check if an email is present in /users/accountDeleted.
 * Returns true if there is an entry for this email.
 */
async function isEmailMarkedDeleted(rawEmail) {
  const normalizedEmail = normalizeEmail(rawEmail);
  if (!normalizedEmail) {
    return false;
  }

  try {
    const accountDeletedReference = doc(
      db,
      USERS_COLLECTION,
      ACCOUNT_DELETED_DOC_ID
    );
    const snapshot = await getDoc(accountDeletedReference);

    if (!snapshot.exists()) {
      return false;
    }

    const data = snapshot.data() || {};
    return Boolean(data[normalizedEmail]);
  } catch {
    // If the lookup fails (network/rules), fail open and allow the flow.
    return false;
  }
}

/**
 * Force a user's trial to inactive in their /users/{uid} document.
 */
async function forceTrialInactiveForUser(userId) {
  if (!userId) return;

  try {
    const userReference = doc(db, USERS_COLLECTION, userId);
    await setDoc(
      userReference,
      { isTrialPeriodActive: false },
      { merge: true }
    );
  } catch {
    // Best-effort; errors are swallowed.
  }
}

export default function AiTrialPeriod({
  requireEmailVerified = false,
  onSuccess,
  onAlreadyActive,
  onRequireLogin,
  onRequireVerification
}) {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [checking, setChecking] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const [hasEverStartedTrial, setHasEverStartedTrial] = useState(false);
  const [activeUntil, setActiveUntil] = useState(null);
  const [isTrialActive, setIsTrialActive] = useState(false);

  const [showConsent, setShowConsent] = useState(false);
  const redirectGuardRef = useRef(false);

  const goToNutritionStatus = useCallback(() => {
    sessionStorage.removeItem(SKIP_KEY);
    if (location.pathname !== '/my-nutrition-status') {
      navigate('/my-nutrition-status', { replace: true });
    }
  }, [location.pathname, navigate]);

  const markInactiveIfExpired = async (reference, endDate, isActive) => {
    if (isActive && endDate <= new Date()) {
      try {
        await setDoc(reference, { isTrialPeriodActive: false }, { merge: true });
      } catch {
        // Best-effort only
      }
    }
  };

  const redirectHomeAfterExpire = useCallback(async () => {
    if (redirectGuardRef.current) return;
    redirectGuardRef.current = true;

    try {
      sessionStorage.removeItem(TRIAL_ACTIVE_SS);
    } catch {}

    try {
      if (currentUser?.uid) {
        const reference = doc(db, 'users', currentUser.uid);
        await setDoc(reference, { isTrialPeriodActive: false }, { merge: true });
      }
    } catch {
      // Best-effort only
    }

    if (location.pathname !== REDIRECT_PATH) {
      navigate(REDIRECT_PATH, { replace: true });
    }
  }, [currentUser?.uid, location.pathname, navigate]);

  useEffect(() => {
    let cancelled = false;

    async function readTrialOnce() {
      setChecking(true);
      setError('');
      setHasEverStartedTrial(false);
      setActiveUntil(null);
      setIsTrialActive(false);

      if (!currentUser) {
        setChecking(false);
        return;
      }

      try {
        const uid = currentUser.uid;
        const reference = doc(db, 'users', uid);
        const snapshot = await getDoc(reference);
        const now = new Date();

        if (snapshot.exists()) {
          const data = snapshot.data() || {};
          const startTimestamp =
            data.startTrialPeriodDate instanceof Timestamp
              ? data.startTrialPeriodDate
              : null;
          const endTimestamp =
            data.endTrialPeriodDate instanceof Timestamp
              ? data.endTrialPeriodDate
              : null;
          const activeFlagInDb = data.isTrialPeriodActive === true;

          if (startTimestamp && endTimestamp) {
            const endDate = endTimestamp.toDate();
            await markInactiveIfExpired(reference, endDate, activeFlagInDb);

            const stillActive = activeFlagInDb && endDate > now;

            if (!cancelled) {
              setHasEverStartedTrial(true);
              setActiveUntil(endDate);
              setIsTrialActive(stillActive);
            }

            try {
              if (stillActive) {
                sessionStorage.setItem(TRIAL_ACTIVE_SS, '1');
              } else {
                sessionStorage.removeItem(TRIAL_ACTIVE_SS);
              }
            } catch {}

            const skip = sessionStorage.getItem(SKIP_KEY) === '1';

            if (
              stillActive &&
              !skip &&
              location.pathname !== '/my-nutrition-status'
            ) {
              navigate('/my-nutrition-status', { replace: true });
            }

            if (!stillActive) {
              redirectHomeAfterExpire();
            }
          }
        }
      } catch (exception) {
        if (!cancelled) setError(exception?.message || String(exception));
      } finally {
        if (!cancelled) setChecking(false);
      }
    }

    readTrialOnce();
    return () => {
      cancelled = true;
    };
  }, [currentUser, location.pathname, navigate, redirectHomeAfterExpire]);

  useEffect(() => {
    if (!currentUser) return;

    const reference = doc(db, 'users', currentUser.uid);
    const unsubscribe = onSnapshot(
      reference,
      snapshot => {
        const data = snapshot.data() || {};
        const startTimestamp =
          data.startTrialPeriodDate instanceof Timestamp
            ? data.startTrialPeriodDate
            : null;
        const endTimestamp =
          data.endTrialPeriodDate instanceof Timestamp
            ? data.endTrialPeriodDate
            : null;
        const activeFlagInDb = data.isTrialPeriodActive === true;

        if (!startTimestamp || !endTimestamp) return;

        const endDate = endTimestamp.toDate();
        const now = new Date();
        const stillActive = activeFlagInDb && endDate > now;

        setHasEverStartedTrial(true);
        setActiveUntil(endDate);
        setIsTrialActive(stillActive);

        if (!stillActive) {
          redirectHomeAfterExpire();
        }
      },
      () => {}
    );

    return unsubscribe;
  }, [currentUser, redirectHomeAfterExpire]);

  useEffect(() => {
    if (!currentUser || !activeUntil) return;

    const millisecondsUntilExpiry = activeUntil.getTime() - Date.now();

    if (millisecondsUntilExpiry <= 0) {
      redirectHomeAfterExpire();
      return;
    }

    let longChunkTimerId = 0;
    let finalTimerId = 0;

    const scheduleFinal = delayMilliseconds => {
      finalTimerId = window.setTimeout(() => {
        redirectHomeAfterExpire();
      }, Math.max(0, delayMilliseconds + 250));
    };

    if (millisecondsUntilExpiry <= MAX_TIMEOUT_MS) {
      scheduleFinal(millisecondsUntilExpiry);
    } else {
      const tick = () => {
        const remaining = activeUntil.getTime() - Date.now();
        if (remaining <= MAX_TIMEOUT_MS) {
          clearTimeout(longChunkTimerId);
          scheduleFinal(remaining);
          return;
        }
        longChunkTimerId = window.setTimeout(tick, MAX_TIMEOUT_MS);
      };
      longChunkTimerId = window.setTimeout(tick, MAX_TIMEOUT_MS);
    }

    return () => {
      if (longChunkTimerId) clearTimeout(longChunkTimerId);
      if (finalTimerId) clearTimeout(finalTimerId);
    };
  }, [currentUser, activeUntil, redirectHomeAfterExpire]);

  const startTrialAfterConsent = useCallback(
    async () => {
      setError('');

      if (!currentUser) {
        onRequireLogin?.();
        setError('Please sign in to start your trial.');
        return;
      }
      if (requireEmailVerified && !currentUser.emailVerified) {
        onRequireVerification?.();
        setError('Please verify your email to start your trial.');
        return;
      }

      setSaving(true);
      try {
        // 🔒 NEW: Check if this email is in /users/accountDeleted
        const emailIsDeleted = await isEmailMarkedDeleted(currentUser.email);
        if (emailIsDeleted) {
          await forceTrialInactiveForUser(currentUser.uid);

          try {
            sessionStorage.removeItem(TRIAL_ACTIVE_SS);
          } catch {}

          // Treat as "trial ended / blocked" in local state
          setHasEverStartedTrial(true);
          setActiveUntil(null);
          setIsTrialActive(false);
          setShowConsent(false);

          // Intentionally do not set an error message here so no banner is shown.
          return;
        }

        const uid = currentUser.uid;
        const reference = doc(db, 'users', uid);

        const snapshot = await getDoc(reference);
        const now = new Date();

        if (snapshot.exists()) {
          const data = snapshot.data() || {};
          const hasTrialFields =
            data.startTrialPeriodDate instanceof Timestamp &&
            data.endTrialPeriodDate instanceof Timestamp;

          if (hasTrialFields) {
            const endDate = data.endTrialPeriodDate.toDate();
            if (data.isTrialPeriodActive && endDate > now) {
              try {
                sessionStorage.setItem(TRIAL_ACTIVE_SS, '1');
              } catch {}
              onAlreadyActive?.(endDate);
              goToNutritionStatus();
            }

            setHasEverStartedTrial(true);
            setActiveUntil(endDate);
            setIsTrialActive(data.isTrialPeriodActive && endDate > now);
            setShowConsent(false);
            return;
          }
        }

        const endDate = new Date(Date.now() + 15 * 24 * 60 * 60 * 1000);

        await setDoc(
          reference,
          {
            startTrialPeriodDate: serverTimestamp(),
            endTrialPeriodDate: Timestamp.fromDate(endDate),
            isTrialPeriodActive: true
          },
          { merge: true }
        );

        setHasEverStartedTrial(true);
        setActiveUntil(endDate);
        setIsTrialActive(true);
        setShowConsent(false);

        try {
          sessionStorage.setItem(TRIAL_ACTIVE_SS, '1');
        } catch {}

        onSuccess?.({ endDate });
        goToNutritionStatus();
      } catch (exception) {
        setError(exception?.message || String(exception));
      } finally {
        setSaving(false);
      }
    },
    [
      currentUser,
      goToNutritionStatus,
      onAlreadyActive,
      onRequireLogin,
      onRequireVerification,
      onSuccess,
      requireEmailVerified
    ]
  );

  const handleStartTrialClick = () => {
    setError('');

    if (!currentUser) {
      onRequireLogin?.();
      setError('Please sign in to start your trial.');
      return;
    }

    if (requireEmailVerified && !currentUser.emailVerified) {
      onRequireVerification?.();
      setError('Please verify your email to start your trial.');
      return;
    }

    if (hasEverStartedTrial) {
      if (activeUntil && activeUntil > new Date()) {
        try {
          sessionStorage.setItem(TRIAL_ACTIVE_SS, '1');
        } catch {}
        onAlreadyActive?.(activeUntil);
        goToNutritionStatus();
      }
      return;
    }

    setShowConsent(true);
  };

  const handleUpgradeClick = () => {
    sessionStorage.setItem(SKIP_KEY, '1');
    navigate('/home');
  };

  const handleContinueTrialClick = () => {
    sessionStorage.removeItem(SKIP_KEY);
    navigate('/my-nutrition-status');
  };

  if (checking) return null;

  const userName =
    currentUser?.displayName ||
    currentUser?.email?.split('@')[0] ||
    'there';

  return (
    <div className="ai-trialPeriod">
      {error && (
        <div className="ai-trialPeriod__error alert alert-danger">
          {error}
        </div>
      )}

      <div className="ai-trialPeriod__actions">
        {!hasEverStartedTrial && (
          <button
            className="button ai-trialPeriod__button"
            onClick={handleStartTrialClick}
            disabled={saving}
          >
            {saving ? 'Starting…' : 'Start Free Trial'}
          </button>
        )}

        {hasEverStartedTrial &&
          isTrialActive &&
          location.pathname === '/my-nutrition-status' && (
            <button
              className="button ai-trialPeriod__button"
              onClick={handleUpgradeClick}
              aria-label="Go to Home to upgrade"
            >
              Upgrade
            </button>
          )}

        {hasEverStartedTrial &&
          isTrialActive &&
          location.pathname !== '/my-nutrition-status' && (
            <button
              className="button ai-trialPeriod__button"
              onClick={handleContinueTrialClick}
              aria-label="Continue Free Trial"
            >
              Continue Free Trial
            </button>
          )}

        {hasEverStartedTrial && !isTrialActive && (
          <button
            className="button ai-trialPeriod__button"
            onClick={() => navigate('/home')}
            aria-label="Upgrade after trial ended"
          >
            Trial ended — Upgrade
          </button>
        )}
      </div>

      {showConsent && (
        <AiOverLay
          userName={userName}
          onClose={() => setShowConsent(false)}
          onConsent={startTrialAfterConsent}
        />
      )}
    </div>
  );
}
